import { Link } from 'react-router-dom';
import { 
  Shield, Award, Users, Truck, CheckCircle, 
  FileCheck, Wrench, Package, Clock, ArrowRight, Phone
} from 'lucide-react';

const Safety = () => {
  const safetyStandards = [
    {
      icon: Users,
      title: 'Driver Standards',
      items: [
        'Rigorous hiring process with background checks',
        'CDL-A license with clean driving records',
        'Ongoing safety training and certifications',
        'Drug and alcohol testing compliance',
        'Hours of Service (HOS) compliance',
      ],
    },
    {
      icon: Wrench,
      title: 'Equipment Maintenance',
      items: [
        'Regular preventive maintenance schedules',
        'Pre-trip and post-trip inspections',
        'DOT annual inspections on all equipment',
        'Immediate repair of any safety issues',
        'Modern, well-maintained fleet',
      ],
    },
    {
      icon: Package,
      title: 'Cargo Securement',
      items: [
        'Proper load securement training',
        'Quality straps, chains, and tarps',
        'Weight distribution compliance',
        'Hazmat certification when required',
        'Regular equipment inspections',
      ],
    },
    {
      icon: Clock,
      title: 'Expedited & Team Safety',
      items: [
        'Coordinated team driving schedules',
        'Rest period compliance for solo and team',
        'Real-time tracking and communication',
        'Emergency response protocols',
        '24/7 dispatch support',
      ],
    },
  ];

  return (
    <div className="bg-white" data-testid="safety-page">
      {/* Hero Header */}
      <section className="bg-[#0B2A5B] py-20" data-testid="safety-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Our Commitment
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight font-['Montserrat'] mb-4">
            Safety & Compliance
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Safety is our top priority. We maintain the highest standards of compliance and operational safety in the industry.
          </p>
        </div>
      </section>

      {/* Safety Commitment Section */}
      <section className="section-padding" data-testid="safety-commitment">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Our Promise
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A5B] tracking-tight mb-6 font-['Montserrat']">
                Safety is Our Foundation
              </h2>
              <p className="text-[#6B7280] mb-6 leading-relaxed">
                At Douala Express Inc., safety isn't just a policy—it's our culture. Every decision we make, from hiring drivers to maintaining equipment, prioritizes the safety of our team, your cargo, and the public.
              </p>
              <p className="text-[#6B7280] leading-relaxed">
                We invest in ongoing training, modern equipment, and comprehensive safety protocols to ensure every shipment is handled with the utmost care and professionalism.
              </p>
            </div>
            <div>
              <img
                src="/11.jpeg"
                alt="Safety Operations"
                className="w-full h-[350px] object-cover rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Highlight Box */}
      <section className="bg-[#F8FAFC] py-16" data-testid="compliance-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="compliance-badge text-white p-8 md:p-12 rounded-sm">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="h-8 w-8 text-[#FFD700]" />
                  <h3 className="text-2xl font-bold font-['Montserrat']">
                    Federal Compliance
                  </h3>
                </div>
                <p className="text-white/80 mb-6">
                  Douala Express Inc. is a fully authorized interstate motor carrier, registered with the Federal Motor Carrier Safety Administration (FMCSA).
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-[#FFD700]" />
                    <span className="text-sm">Fully Insured</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileCheck className="h-5 w-5 text-[#FFD700]" />
                    <span className="text-sm">FMCSA Registered</span>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 p-6 rounded-sm">
                <h4 className="text-[#FFD700] text-sm font-bold uppercase tracking-wider mb-4">
                  Authority Information
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/70">USDOT Number</span>
                    <span className="text-white font-bold text-xl font-['Montserrat']">2520499</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">MC Number</span>
                    <span className="text-white font-bold text-xl font-['Montserrat']">874923</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Standards Grid */}
      <section className="section-padding" data-testid="safety-standards">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Standards
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A5B] tracking-tight font-['Montserrat']">
              Comprehensive Safety Protocols
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {safetyStandards.map((standard, index) => (
              <div
                key={index}
                className="bg-[#F8FAFC] p-8 rounded-sm border-l-4 border-[#0B2A5B] hover:border-[#C62828] transition-colors"
                data-testid={`safety-standard-${index}`}
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-[#0B2A5B] p-3 rounded-sm">
                    <standard.icon className="h-6 w-6 text-[#FFD700]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0B2A5B] font-['Montserrat']">
                    {standard.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {standard.items.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="h-4 w-4 text-[#C62828] mt-1 flex-shrink-0" />
                      <span className="text-[#6B7280] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-banner py-16" data-testid="safety-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Questions About Our Safety Standards?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            We're happy to discuss our safety protocols and compliance documentation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-[#C62828] px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-slate-100 transition-all shadow-lg inline-flex items-center justify-center space-x-2"
              data-testid="safety-cta-contact"
            >
              <span>Contact Us</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:773-344-7748"
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#C62828] transition-all inline-flex items-center justify-center space-x-2"
              data-testid="safety-cta-call"
            >
              <Phone className="h-4 w-4" />
              <span>773-344-7748</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Safety;
